'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Mail, Send, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (value: string) => {
    if (!value.trim()) return 'El correo es obligatorio';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Ingresa un correo válido';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    setEmailError('');
    setIsLoading(true);

    // BACKEND INTEGRATION POINT: POST /api/auth/forgot-password
    // Validate email exists, generate secure token, send reset link
    await new Promise((res) => setTimeout(res, 1400));

    setIsLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="card-elevated p-8 rounded-2xl">
        {/* Success state */}
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-pastel-purple flex items-center justify-center">
            <CheckCircle2 size={32} className="text-primary" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              ¡Correo enviado!
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Si el correo ingresado está registrado, recibirás un enlace para restablecer tu contraseña. Revisa también tu carpeta de spam o correo no deseado.
            </p>
          </div>

          <div className="w-full p-4 rounded-xl bg-pastel-purple border border-border">
            <p className="text-xs text-primary font-medium">
              📧 Enlace enviado a: <span className="font-bold">{email}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              El enlace expirará en 60 minutos y solo puede usarse una vez.
            </p>
          </div>

          <button
            type="button"
            onClick={() => { setSubmitted(false); setEmail(''); }}
            className="text-sm text-primary font-medium hover:underline transition-all"
          >
            Enviar a otro correo
          </button>

          <p className="text-sm text-muted-foreground">
            ¿Recordaste tu contraseña?{' '}
            <Link href="/login-screen" className="text-primary font-semibold hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-elevated p-8 rounded-2xl">
      {/* Header */}
      <div className="mb-7">
        <div className="w-12 h-12 rounded-2xl brand-gradient flex items-center justify-center shadow-brand mb-4">
          <Mail size={22} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">
          Recuperar contraseña
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Mail size={14} className="text-primary" />
            Correo electrónico
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Ingresa tu correo electrónico"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(validateEmail(e.target.value));
            }}
            className="input-base"
          />
          {emailError && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-0.5">
              <AlertCircle size={12} />
              {emailError}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full mt-1"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
              </svg>
              Enviando enlace...
            </>
          ) : (
            <>
              <Send size={16} />
              Enviar enlace
            </>
          )}
        </button>
      </form>

      {/* Back to login */}
      <div className="mt-5 flex flex-col items-center gap-3">
        <p className="text-sm text-muted-foreground">
          ¿Recordaste tu contraseña?{' '}
          <Link href="/login-screen" className="text-primary font-semibold hover:underline">
            Inicia sesión
          </Link>
        </p>
        <Link
          href="/login-screen"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-medium"
        >
          <ArrowLeft size={13} />
          Volver al inicio de sesión
        </Link>
      </div>
    </div>
  );
}
